import { createFileRoute, Link } from "@tanstack/react-router";
import { loadAuthor } from "~/utils/content-fns";
import { seo, seoLinks, breadcrumbJsonLd, personJsonLd } from "~/utils/seo";
import { Breadcrumbs } from "~/components/Breadcrumbs";
import { AuthorAvatar } from "~/components/AuthorAvatar";
import type { AuthorCredit } from "~/types/views";

/** Shared by the visible trail and the BreadcrumbList schema. */
function authorCrumbs(name: string) {
  return [
    { name: "Home", url: "/" },
    { name: "Authors", url: "/authors" },
    { name },
  ];
}

/**
 * A byline page is a person, so the description leads with who they are.
 * Falls back to the title when no bio has been written yet, rather than
 * emitting an empty meta description.
 */
function metaDescription(name: string, title: string, bio: string, count: number) {
  if (bio) return bio.length > 300 ? `${bio.slice(0, 297).trimEnd()}...` : bio;
  const role = title ? `${title} at C1` : "C1";
  return `${name}, ${role}. ${count} ${count === 1 ? "article" : "articles"} on identity security, access governance, and AI agents.`;
}

export const Route = createFileRoute("/authors/$slug")({
  loader: ({ params }) => loadAuthor({ data: { slug: params.slug } }),
  head: ({ loaderData }) => {
    if (!loaderData) return { meta: [], links: [], scripts: [] };
    const url = `/authors/${loaderData.key}`;
    const description = metaDescription(
      loaderData.name,
      loaderData.title,
      loaderData.bio,
      loaderData.credits.length,
    );
    return {
      meta: seo({
        title: `${loaderData.name}${loaderData.title ? `, ${loaderData.title}` : ""}`,
        description,
        image: loaderData.image || null,
        url,
      }),
      links: seoLinks({ url }),
      scripts: [
        personJsonLd({
          url,
          name: loaderData.name,
          jobTitle: loaderData.title,
          description: loaderData.bio,
          image: loaderData.image,
          sameAs: loaderData.linkedin ? [loaderData.linkedin] : [],
        }),
        breadcrumbJsonLd(authorCrumbs(loaderData.name)),
      ].filter((s) => s !== null),
    };
  },
  component: AuthorPage,
});

const COLLECTION_LABEL: Record<AuthorCredit["collection"], string> = {
  blog: "Blog",
  engineering: "Engineering",
  guides: "Guide",
  glossary: "Glossary",
};

function CreditCard({ credit }: { credit: AuthorCredit }) {
  const href = `/${credit.collection}/${credit.slug}`;
  const date = new Date(credit.date).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });

  return (
    <li className="group relative flex gap-5 border-b border-gray-20 py-6 first:pt-0">
      {credit.featured_image && (
        <div className="hidden w-40 shrink-0 overflow-hidden rounded-md sm:block">
          <img
            src={credit.featured_image}
            // The heading below is the link text; repeating it here would make
            // screen readers announce the same title twice.
            alt=""
            loading="lazy"
            decoding="async"
            className="aspect-[1200/630] h-full w-full object-cover"
          />
        </div>
      )}
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-3 text-xs font-semibold uppercase tracking-wide text-gray-500">
          <span>{COLLECTION_LABEL[credit.collection]}</span>
          <time dateTime={new Date(credit.date).toISOString().slice(0, 10)}>{date}</time>
        </div>
        <h2 className="mt-2 font-body text-lg font-bold leading-snug text-charcoal">
          {/* Link wraps the title so it carries real anchor text; the
              after:absolute makes the whole row clickable. */}
          <a
            href={href}
            className="after:absolute after:inset-0 after:content-[''] group-hover:text-teal-dark"
          >
            {credit.title}
          </a>
        </h2>
        {credit.description && (
          <p className="mt-1.5 line-clamp-2 text-sm leading-relaxed text-gray-80">
            {credit.description}
          </p>
        )}
      </div>
    </li>
  );
}

function AuthorPage() {
  const author = Route.useLoaderData();
  const count = author.credits.length;

  return (
    <div className="px-6 md:px-8">
      <div className="mx-auto max-w-screen-lg py-10 md:py-14">
        <Breadcrumbs items={authorCrumbs(author.name)} />

        {/* ---------- Identity card ---------- */}
        <div className="mt-6 flex flex-col gap-6 rounded-2xl border border-[#DCDADA] bg-white p-6 sm:flex-row sm:items-start md:p-8">
          <AuthorAvatar
            name={author.name}
            image={author.image}
            className="h-24 w-24 shrink-0 text-2xl"
          />
          <div className="min-w-0">
            <h1 className="font-display text-3xl font-bold text-charcoal md:text-4xl">
              {author.name}
            </h1>
            {author.title && (
              <p className="mt-1 font-body text-base text-gray-80">{author.title}, C1.ai</p>
            )}
            {author.bio && (
              <p className="mt-4 max-w-[52rem] text-base leading-relaxed text-gray-80">
                {author.bio}
              </p>
            )}
            {author.linkedin && (
              <a
                href={author.linkedin}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-teal-dark underline underline-offset-2"
              >
                LinkedIn
                <span aria-hidden="true">↗</span>
              </a>
            )}
          </div>
        </div>

        {/* ---------- Everything they've published ---------- */}
        <h2 className="mt-12 font-display text-2xl font-bold text-charcoal">
          {count === 1 ? "1 article" : `${count} articles`} by {author.name}
        </h2>
        <ul className="mt-4">
          {author.credits.map((c) => (
            <CreditCard key={`${c.collection}/${c.slug}`} credit={c} />
          ))}
        </ul>

        <div className="mt-10">
          <Link
            to="/authors"
            className="text-sm font-semibold text-teal-dark underline underline-offset-2"
          >
            All C1.ai authors
          </Link>
        </div>
      </div>
    </div>
  );
}
